import React from 'react';
import { User } from '../../../../../services/eventService';
import styles from '../../EventView.module.css';

interface UnmatchedUsersModalProps {
  show: boolean;
  onClose: () => void;
  unmatchedUsers: User[];
  selectedRoundForUnmatched: number | null;
  handleDragStart: (e: React.DragEvent, user: User) => void;
  handleShowUnmatchedUsers: () => void;
}

const UnmatchedUsersModal: React.FC<UnmatchedUsersModalProps> = ({
  show,
  onClose,
  unmatchedUsers,
  selectedRoundForUnmatched,
  handleDragStart,
  handleShowUnmatchedUsers
}) => {
  if (!show) return null;

  return (
    <div className="modal show d-block" tabIndex={-1}>
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header bg-light">
            <h5 className="modal-title">
              <i className="bi bi-person-plus-fill me-2"></i>
              Unmatched Users
              {selectedRoundForUnmatched !== null && ` - Round ${selectedRoundForUnmatched}`}
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
            ></button>
          </div>
          <div className="modal-body">
            {unmatchedUsers.length > 0 ? (
              <>
                <div className="alert alert-info">
                  <i className="bi bi-info-circle me-2"></i>
                  <strong>{unmatchedUsers.length}</strong> users are not yet assigned to a group.
                  You can drag and drop them into groups or click to add them.
                </div>
                
                <div className="row row-cols-1 row-cols-md-2 g-3">
                  {unmatchedUsers.map(user => (
                    <div key={user.User_ID} className="col">
                      <div 
                        className={`card ${styles.unmatchedUserCard}`}
                        draggable
                        onDragStart={(e) => handleDragStart(e, user)}
                      >
                        <div className="card-body d-flex align-items-center">
                          {/* Profile Image */}
                          <div className={styles.unmatchedUserAvatar}>
                            {user.picture ? (
                              <img 
                                src={`data:image/png;base64,${user.picture}`} 
                                alt="User Image" 
                              />
                            ) : (
                              <div className={styles.defaultAvatar}>
                                <i className="bi bi-person-fill"></i>
                              </div>
                            )}
                          </div>
                          
                          <div className="flex-grow-1 ms-3">
                            <div className="fw-bold">
                              {user.firstname} {user.lastname}
                            </div>
                            {user.jobtitle && (
                              <small className="text-muted d-block">{user.jobtitle}</small>
                            )}
                            {user.firm && (
                              <small className="text-primary">({user.firm.abbrev})</small>
                            )}
                          </div>
                          
                          <div className={styles.dragHandle}>
                            <i className="bi bi-grip-vertical text-muted"></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-5">
                <i className="bi bi-check-circle-fill text-success" style={{fontSize: '3rem'}}></i>
                <h5 className="text-success mt-3">All users are matched!</h5>
                <p className="text-muted">
                  {selectedRoundForUnmatched !== null 
                    ? `All users are assigned to groups in Round ${selectedRoundForUnmatched}.`
                    : 'All registered users have been assigned to groups.'
                  }
                </p>
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button 
              type="button" 
              className="btn btn-secondary" 
              onClick={onClose}
            >
              Close
            </button>
            {selectedRoundForUnmatched !== null && (
              <button 
                type="button" 
                className="btn btn-primary"
                onClick={() => handleShowUnmatchedUsers()}
              >
                Show All Unmatched
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UnmatchedUsersModal;
